import { assertNeverSafely, PlanStateSchema } from '@aline/common';
import type { ApolloCache, ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import { debugLogs } from 'src/config';
import { Apollo } from 'src/gql/generated';
import { mergePlanStateSnapshot } from 'src/gql/merge-plan-snapshot';
import { agentThreadTypename } from 'src/helpers';

import { applyArtifactCreatedEventToCache } from './apply-artifact-created-event';
import { applyReportArtifactCreatedEventToCache } from './apply-report-artifact-created-event';
import { applySkillArtifactCreatedEventToCache } from './apply-skill-artifact-created-event';
import { applyWorkflowArtifactCreatedEventToCache } from './apply-workflow-artifact-created-event';
import { getHasVisibleHarnessWork, getIsTerminalAssistantStatus } from './harness-visible-work';
import { $chatThreadState } from '../chat-state';

const AGENT_MESSAGE_FRAGMENT = gql`
  fragment StreamingAgentMessageFields on AgentMessage {
    id
    status
    text
  }
`;

const MESSAGE_DATA_FRAGMENT = gql`
  fragment StreamingMessageData on AgentMessage {
    id
    data
  }
`;

const MESSAGE_PLAN_STATE_FRAGMENT = gql`
  fragment StreamingMessagePlanState on AgentMessage {
    id
    planState
  }
`;

const THREAD_TITLE_FRAGMENT = gql`
  fragment ThreadTitle on AgentThread {
    id
    title
  }
`;

/**
 * Background refresh the user did not ask for, so a failure is reported rather than
 * surfaced: the artifact simply appears on the next load instead.
 */
function refetchThread(client: ApolloClient, threadId: string): void {
  client
    .query({
      errorPolicy: 'none',
      fetchPolicy: 'network-only',
      query: Apollo.GetAgentThreadDocument,
      variables: { id: threadId },
    })
    .catch((error: unknown) => {
      Sentry.captureException(error, {
        extra: { threadId },
        tags: { component: 'chat-harness-skill-refetch' },
      });
    });
}

/**
 * Wrapper around Apollo's writeFragment that fills in the AgentMessage
 * `__typename` and computes the cache id once. Three of the four cache
 * writes in this hook target AgentMessage with the same identify shape.
 */
function writeAgentMessageFragment<T extends { id: string }>(
  cache: ApolloCache,
  fragment: DocumentNode,
  data: T
): void {
  cache.writeFragment({
    data: { __typename: 'AgentMessage', ...data },
    fragment,
    id: cache.identify({ __typename: 'AgentMessage', id: data.id }),
  });
}

function writeAgentMessagePlanState(
  cache: ApolloCache,
  messageId: string,
  planState: unknown
): void {
  const id = cache.identify({ __typename: 'AgentMessage', id: messageId });
  const current = cache.readFragment<{ planState: unknown }>({
    fragment: MESSAGE_PLAN_STATE_FRAGMENT,
    id,
  });

  writeAgentMessageFragment(cache, MESSAGE_PLAN_STATE_FRAGMENT, {
    id: messageId,
    planState: mergePlanStateSnapshot(current?.planState, planState),
  });
}

/**
 * Per-message wall-clock anchor used by the dev timing logger. The first
 * event of a turn sets the anchor; every subsequent event is annotated
 * with `+<elapsed>ms`. Entries are evicted on terminal `AssistantMessageEvent`
 * (Complete / Errored / Cancelled) so a long-lived tab's session doesn't
 * accumulate one entry per turn forever.
 */
const turnStartByMessage = new Map<string, number>();

function evictTurnTiming(messageId: string): void {
  turnStartByMessage.delete(messageId);
}

function logTurnTiming(messageId: string, label: string, extra?: Record<string, unknown>): void {
  if (!debugLogs) return;

  const now = performance.now();
  const turnStart = turnStartByMessage.get(messageId) ?? now;
  turnStartByMessage.set(messageId, turnStart);

  const elapsed = `+${Math.round(now - turnStart)}ms`.padStart(8);
  const message = `[harness-timing] ${elapsed} ${label}`;
  extra ? console.log(message, extra) : console.log(message);
}

function clearThinkingMessage(messageId: string): void {
  if ($chatThreadState.thinkingMessage?.messageId !== messageId) return;
  $chatThreadState.thinkingMessage = null;
}

/**
 * Subscribes to agent events from the server.
 * Writes to Apollo cache (source of truth) for messages, tool status, and titles.
 * Uses Valtio only for ephemeral UI state (thinking message).
 */
export function useStreamingAgentEvents(): void {
  Apollo.useOnAgentEventSubscription({
    ignoreResults: true,
    onData: ({ client, data }) => {
      const event = data.data?.onAgentEvent;
      if (!event) return;

      /**
       * Wrap the whole handler body: a throw inside any case (cache
       * write, set-state, assertNeverSafely) would bubble up to Apollo
       * and break the subscription for the rest of the session — no
       * more chat events would arrive. Capture and continue so a single
       * malformed payload can't wedge the channel; Sentry sees the
       * underlying cause with the event __typename for triage.
       */
      try {
        switch (event.__typename) {
          case 'AssistantMessageEvent': {
            const { message } = event;
            logTurnTiming(message.id, `AssistantMessage status=${message.status}`);
            writeAgentMessageFragment(client.cache, AGENT_MESSAGE_FRAGMENT, {
              id: message.id,
              status: message.status,
              text: message.text,
            });
            if (getIsTerminalAssistantStatus(message.status)) {
              clearThinkingMessage(message.id);
              evictTurnTiming(message.id);
            }
            return;
          }
          case 'LoadingEvent': {
            $chatThreadState.thinkingMessage = {
              messageId: event.messageId,
              text: event.thinkingMessage,
            };
            return;
          }
          case 'ToolStatusUpdateEvent': {
            const { data: messageData, messageId } = event;
            writeAgentMessageFragment(client.cache, MESSAGE_DATA_FRAGMENT, {
              data: messageData,
              id: messageId,
            });
            clearThinkingMessage(messageId);
            return;
          }
          case 'TitleUpdateEvent': {
            const { threadId, title } = event;
            client.cache.writeFragment({
              data: {
                __typename: agentThreadTypename,
                id: threadId,
                title,
              },
              fragment: THREAD_TITLE_FRAGMENT,
              id: client.cache.identify({ __typename: agentThreadTypename, id: threadId }),
            });
            return;
          }
          case 'PlanSnapshotEvent': {
            const { messageId, planJson } = event;
            logTurnTiming(messageId, 'PlanSnapshotEvent');
            /**
             * Validate at the boundary: only valid PlanState payloads land
             * in the cache, so PlanTree can trust message.planState without
             * re-validating per render.
             */
            const result = PlanStateSchema.safeParse(planJson);
            if (!result.success) {
              console.warn(
                `[chat-harness] dropping invalid PlanSnapshotEvent for message ${messageId}:`,
                result.error
              );
              /**
               * Schema drift between server and client makes the chat UI
               * silently freeze on the previous snapshot. The console.warn
               * is invisible in production — Sentry catches the drift.
               */
              Sentry.captureMessage('PlanSnapshotEvent failed validation', {
                extra: { error: result.error.format(), messageId },
                level: 'warning',
              });
              return;
            }
            /**
             * Opt-in visibility: print each snapshot so the plan structure
             * (and growing nodeOutputs) is inspectable without opening the cache.
             * JSON.stringify with indent renders fully-expanded text (no
             * click-to-drill-in needed) — preferred for debugging stage
             * context flow at a glance.
             */
            if (debugLogs) {
              console.log(
                `[chat-harness] PlanSnapshotEvent for ${messageId}:\n` +
                  JSON.stringify(result.data, null, 2)
              );
            }
            writeAgentMessagePlanState(client.cache, messageId, result.data);
            if (getHasVisibleHarnessWork(result.data)) clearThinkingMessage(messageId);
            return;
          }
          case 'ArtifactCreatedEvent': {
            applyArtifactCreatedEventToCache(client.cache, event);
            return;
          }
          case 'ReportArtifactCreatedEvent': {
            applyReportArtifactCreatedEventToCache(client.cache, event);
            return;
          }
          case 'SkillArtifactCreatedEvent': {
            if (!applySkillArtifactCreatedEventToCache(client.cache, event))
              refetchThread(client, event.threadId);
            return;
          }
          case 'WorkflowArtifactCreatedEvent': {
            applyWorkflowArtifactCreatedEventToCache(client.cache, event);
            return;
          }
          default:
            assertNeverSafely(event);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: { __typename: event.__typename },
          tags: { component: 'chat-harness-stream-handler' },
        });
      }
    },
    onError: error => {
      /**
       * Subscription transport failures (websocket drop, auth blip)
       * silently froze the chat panel — no events, no surface. Capture
       * to Sentry and clear the thinking pulse so the user isn't
       * staring at a "Working on request..." spinner that will never
       * resolve. Apollo's `shouldResubscribe: true` reconnects in the
       * background; once it does, fresh events flow normally.
       */
      Sentry.captureException(error, {
        tags: { component: 'chat-harness-subscription' },
      });
      $chatThreadState.thinkingMessage = null;
    },
    shouldResubscribe: true,
  });
}
